import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { cn } from '@siliconx/shared/ui/utils';
import type { WizardStep, WizardStepId } from '../lib/addAssetWizardSteps';

/**
 * SIL-1183: sticky progress bar for the individual-org Add Asset wizard.
 * Pinned under the page header (`sticky top-0`), stays visible while the
 * user scrolls the form.
 *
 * `steps` is whatever `computeWizardSteps` (lib/addAssetWizardSteps.ts)
 * returned - this component renders that state, it never derives it, so the
 * bar and the form's inline section numbers can't drift apart.
 */
export interface AddAssetStepperProps {
  steps: WizardStep[];
  /** false while the success panel is showing - no step is clickable then. */
  interactive: boolean;
  onStepClick: (stepId: WizardStepId) => void;
  /**
   * SIL-1812: during setup the AppShell setup rail shows these steps at `lg+`,
   * so this bar is hidden there (display:none, out of the a11y tree) to keep
   * the progressbar semantics in exactly one visible place. Mobile unaffected.
   */
  hideAtDesktop?: boolean;
}

export function AddAssetStepper({ steps, interactive, onStepClick, hideAtDesktop = false }: AddAssetStepperProps) {
  const currentIndex = steps.findIndex((s) => s.current);
  const stepNumber = currentIndex === -1 ? steps.length : currentIndex + 1;
  const currentStep = steps[currentIndex] ?? steps[steps.length - 1];
  const pct = steps.length > 0 ? (stepNumber / steps.length) * 100 : 0;

  // Polite live-region announcement, fired only when the current step
  // actually changes (not on every render) - a step id + label pair is a
  // stable enough key since a step's label never changes mid-session.
  const [announcement, setAnnouncement] = useState('');
  const lastAnnouncedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!currentStep) return;
    const text = `Step ${stepNumber} of ${steps.length}: ${currentStep.label}`;
    if (lastAnnouncedRef.current === text) return;
    lastAnnouncedRef.current = text;
    setAnnouncement(text);
  }, [currentStep, stepNumber, steps.length]);

  if (steps.length === 0) return null;

  return (
    <div
      role="progressbar"
      aria-valuenow={stepNumber}
      aria-valuemin={1}
      aria-valuemax={steps.length}
      aria-label="Add asset progress"
      className={cn(
        // SIL-1812: solid card background - no translucency or blur, so page
        // content scrolling underneath never shows through the text.
        'sticky top-0 z-30 -mx-4 mb-5 border-b border-border bg-card px-4 py-3 sm:mx-0 sm:rounded-xl sm:border sm:px-4 sm:shadow-sm',
        hideAtDesktop && 'lg:hidden',
      )}
    >
      {/* Full bar - phone width and up collapses this in favor of the
          compact readout below. */}
      <div className="hidden items-center gap-2 sm:flex">
        {steps.map((step, i) => (
          <div key={step.id} className="flex flex-1 items-center gap-2 last:flex-none">
            <button
              type="button"
              disabled={!interactive || !step.complete}
              aria-current={step.current ? 'step' : undefined}
              onClick={() => onStepClick(step.id)}
              className={cn(
                'flex items-center gap-2 rounded-md px-1.5 py-1 text-xs font-semibold transition-colors',
                step.complete && interactive ? 'hover:bg-muted' : 'cursor-default',
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'flex h-5 w-5 flex-none items-center justify-center rounded-full font-num text-[13px]',
                  step.complete
                    ? 'bg-emerald-600 text-white'
                    : step.current
                      ? 'bg-brand-600 text-white'
                      : 'bg-muted text-muted-foreground',
                )}
              >
                {step.complete ? <Check className="h-3 w-3" /> : i + 1}
              </span>
              <span
                className={cn(
                  step.current || step.complete ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {step.label}
              </span>
            </button>
            {i < steps.length - 1 && <span aria-hidden="true" className="h-px flex-1 bg-border" />}
          </div>
        ))}
      </div>

      {/* Collapsed readout below phone width: "Step 2 of 4 · Identify" + a slim determinate bar. */}
      <div className="sm:hidden">
        <p className="text-xs font-semibold text-foreground">
          Step {stepNumber} of {steps.length} &middot; {currentStep?.label}
        </p>
        <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-muted">
          <div
            className={cn(
              'h-full rounded-full transition-[width] duration-200 motion-reduce:transition-none',
              // SIL-1184: one accent per view - once the success panel is
              // showing (interactive=false), the stepper itself is no
              // longer the view's focal point, so this fill goes neutral
              // (not brand, not another accent color).
              interactive ? 'bg-brand-600' : 'bg-muted-foreground/40',
            )}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>
    </div>
  );
}
